import { useEffect, useState } from 'react'
import { Plus, Trash2 } from 'lucide-react'
import { ErrorStrip, LoadingBlock, Note } from '@/shared/ui/states'
import { Modal, Field } from '@/shared/ui/Modal'
import { useCan } from '@/shared/auth/session'
import { fmtDate } from '@/shared/lib/format'
import { explain } from '@/shared/api/problem'
import type { MilestoneResponse, ProgramResponse } from '@/shared/api/types'
import { createMilestone, deleteMilestone, listMilestones, updateMilestone } from '../api'
import {
  MILESTONE_STATUS_ORDER, MilestoneStatusBadge, PRIORITY_ORDER, PriorityBadge,
  milestoneStatusLabel, priorityLabel,
} from '../vocab'
import { DatePicker } from '@/components/erp/date-picker'

/**
 * Milestones — rows with identity, and their own endpoints.
 *
 * PollSphere sent the whole milestone array inside the programme PATCH, so every
 * edit deleted and recreated every milestone and every id changed on every save.
 * Nothing could ever reference one: not a task, not a document, not a comment.
 *
 * Here each milestone has a stable id for its whole life, and editing the
 * programme touches none of them. `ProgramMilestoneIT` asserts exactly that.
 */
export function MilestonesPanel({ program }: { program: ProgramResponse }) {
  const canUpdate = useCan('program:update')
  const [rows, setRows] = useState<MilestoneResponse[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [editing, setEditing] = useState<MilestoneResponse | 'new' | null>(null)

  const load = () => listMilestones(program.id).then(setRows).catch((e) => setError(explain(e)))

  useEffect(() => {
    let alive = true
    listMilestones(program.id)
      .then((r) => { if (alive) setRows(r) })
      .catch((e) => { if (alive) setError(explain(e)) })
    return () => { alive = false }
  }, [program.id])

  const remove = async (m: MilestoneResponse) => {
    if (!window.confirm(`Delete "${m.title}"?`)) return
    try { await deleteMilestone(program.id, m.id); load() }
    catch (e) { setError(explain(e)) }
  }

  if (!rows) return <LoadingBlock label="Loading milestones…" />

  const overdue = rows.filter((m) =>
    m.dueOn && m.statusCode !== 'COMPLETED' && m.statusCode !== 'CANCELLED'
    && new Date(m.dueOn) < new Date())

  return (
    <div className="space-y-3 px-1">
      <ErrorStrip message={error} />
      <div className="flex items-center justify-between gap-2">
        <h4 className="text-erp-sm font-medium">{rows.length} milestone{rows.length === 1 ? '' : 's'}</h4>
        {canUpdate && (
          <button type="button" className="erp-btn h-7 text-erp-xs" onClick={() => setEditing('new')}>
            <Plus className="h-3.5 w-3.5" /> Add
          </button>
        )}
      </div>

      {overdue.length > 0 && (
        <Note tone="bad">
          {overdue.length} milestone{overdue.length === 1 ? ' is' : 's are'} past due and not
          completed.
        </Note>
      )}

      {rows.length === 0 ? (
        <p className="text-erp-xs text-muted-foreground">No milestones recorded.</p>
      ) : (
        <ul className="space-y-2">
          {rows.map((m) => (
            <li key={m.id} className="rounded-md border border-border p-2">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-erp-sm font-medium">{m.title}</span>
                <MilestoneStatusBadge code={m.statusCode} />
                <PriorityBadge code={m.priorityCode} />
                <span className="ml-auto flex items-center gap-1">
                  {canUpdate && (
                    <>
                      <button type="button" className="erp-btn-ghost h-7 text-erp-xs"
                        onClick={() => setEditing(m)}>Edit</button>
                      <button type="button" className="erp-btn-ghost h-7 w-7 p-0"
                        aria-label={`Delete ${m.title}`} onClick={() => remove(m)}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </>
                  )}
                </span>
              </div>
              <p className="text-erp-xs text-muted-foreground">
                {m.dueOn ? `Due ${fmtDate(m.dueOn)}` : 'No due date'}
                {m.completedOn ? ` · completed ${fmtDate(m.completedOn)}` : ''}
                {/* An unowned milestone is a real completeness signal — the
                    quality screen counts them, so the drawer names them. */}
                {!m.ownerUserId && ' · no owner'}
              </p>
              {m.description && <p className="text-erp-xs mt-1">{m.description}</p>}
            </li>
          ))}
        </ul>
      )}

      {editing && (
        <MilestoneDialog
          programId={program.id}
          existing={editing === 'new' ? undefined : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load() }}
        />
      )}
    </div>
  )
}

function MilestoneDialog({ programId, existing, onClose, onSaved }: {
  programId: string; existing?: MilestoneResponse; onClose: () => void; onSaved: () => void
}) {
  const [title, setTitle] = useState(existing?.title ?? '')
  const [description, setDescription] = useState(existing?.description ?? '')
  const [statusCode, setStatus] = useState(existing?.statusCode ?? 'PENDING')
  const [priorityCode, setPriority] = useState(existing?.priorityCode ?? 'MEDIUM')
  const [dueOn, setDueOn] = useState(existing?.dueOn ?? '')
  const [completedOn, setCompletedOn] = useState(existing?.completedOn ?? '')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submit = async () => {
    setBusy(true); setError(null)
    const body = {
      title, description: description || null, statusCode, priorityCode,
      dueOn: dueOn || null, completedOn: completedOn || null,
    }
    try {
      if (existing) await updateMilestone(programId, existing.id, body)
      else await createMilestone(programId, body)
      onSaved()
    } catch (e) { setError(explain(e)) } finally { setBusy(false) }
  }

  return (
    <Modal open onClose={onClose} title={existing ? 'Edit milestone' : 'New milestone'} footer={(
      <>
        <button type="button" className="erp-btn" onClick={onClose}>Cancel</button>
        <button type="button" className="erp-btn-primary" onClick={submit} disabled={busy || !title}>
          {busy ? 'Saving…' : 'Save'}
        </button>
      </>
    )}>
      <div className="space-y-3">
        <ErrorStrip message={error} />
        <Field label="Title" required>
          <input className="erp-input" value={title} maxLength={200}
            onChange={(e) => setTitle(e.target.value)} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Status">
            <select className="erp-input" value={statusCode}
              onChange={(e) => setStatus(e.target.value as typeof statusCode)}>
              {MILESTONE_STATUS_ORDER.map((s) => (
                <option key={s} value={s}>{milestoneStatusLabel(s)}</option>
              ))}
            </select>
          </Field>
          <Field label="Priority">
            <select className="erp-input" value={priorityCode}
              onChange={(e) => setPriority(e.target.value as typeof priorityCode)}>
              {PRIORITY_ORDER.map((s) => <option key={s} value={s}>{priorityLabel(s)}</option>)}
            </select>
          </Field>
          <Field label="Due on">
            <DatePicker value={dueOn ?? ''} onChange={setDueOn} />
          </Field>
          <Field label="Completed on"
            hint={statusCode === 'COMPLETED' ? 'Required when the status is Completed.' : undefined}>
            <DatePicker value={completedOn ?? ''} onChange={setCompletedOn} />
          </Field>
        </div>
        <Field label="Description">
          <textarea className="erp-input min-h-[60px]" value={description ?? ''}
            onChange={(e) => setDescription(e.target.value)} />
        </Field>
      </div>
    </Modal>
  )
}
